import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, User, Check, Loader2, Trash2 } from "lucide-react";
import { FeatureShell, DemoPanel } from "../components/sections/FeatureShell";
import { getProfile, saveProfile, deleteProfile, errorMessage } from "../lib/api";
import { visitorId } from "../lib/stream";

export const DIETS = ["Nut-free", "Vegetarian", "Dairy-free", "Gluten-free", "Halal", "Low sodium"];
const GOALS = ["More vegetables", "Faster weeknights", "Less food waste", "Kids help cook", "Budget-friendly"];

const Chip = ({ on, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-[background-color,border-color,color] duration-300 ${on ? "border-cyan-300 bg-cyan-400/15 text-white" : "border-cyan-400/25 text-slate-300 hover:border-cyan-300/60"}`}>
    {on && <Check className="h-3.5 w-3.5 text-cyan-300" />}{children}
  </button>
);

const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

export default function HouseholdProfile() {
  const [members, setMembers] = useState([{ name: "Dana", age: "38" }, { name: "Maya", age: "7" }]);
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [diets, setDiets] = useState(["Nut-free"]);
  const [goals, setGoals] = useState(["More vegetables", "Faster weeknights"]);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getProfile(visitorId()).then((p) => {
      if (!p) return;
      setMembers(Array.isArray(p.members) ? p.members : []);
      setDiets(Array.isArray(p.diets) ? p.diets : []);
      setGoals(Array.isArray(p.goals) ? p.goals : []);
      setSavedAt(p.updated_at ?? null);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const p = await saveProfile(visitorId(), { members: members.map((m) => ({ name: m.name.slice(0, 40), age: String(m.age).slice(0, 10) })), diets, goals });
      setSavedAt(p.updated_at);
      toast.success("Profile saved. Honesti will use it for your live meal ideas.");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const forget = async () => {
    await deleteProfile(visitorId()).catch(() => {});
    setSavedAt(null);
    toast.success("Saved profile removed.");
  };

  const add = () => {
    if (!name.trim()) return;
    setMembers((m) => [...m, { name: name.trim(), age: age.trim() || "—" }]);
    setName(""); setAge("");
  };

  return (
    <FeatureShell slug="household-profile">
      <DemoPanel badge="Saved to this browser" title="Build your family profile" note="This profile is really saved, linked to this browser only (no account). It pre-fills Honesti's live meal ideas and chat. Don't enter sensitive information.">
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="space-y-8 lg:col-span-7">
            <div>
              <p className="eyebrow mb-4">Who’s at the table</p>
              <div className="flex flex-wrap gap-2">
                {members.map((m, i) => (
                  <span key={`${m.name}-${i}`} className="flex items-center gap-2 rounded-full border border-cyan-400/30 bg-deep/70 py-1.5 pl-3 pr-2 text-sm text-white">
                    <User className="h-3.5 w-3.5 text-cyan-300" />{m.name} · {m.age}
                    <button onClick={() => setMembers((l) => l.filter((_, j) => j !== i))} data-testid={`remove-member-${i}`} aria-label={`Remove ${m.name}`} className="rounded-full p-1 hover:bg-white/10"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" data-testid="member-name-input" className="flex-1 rounded-xl border border-cyan-400/25 bg-deep/70 px-4 py-3 text-white placeholder:text-slate-400 focus:border-cyan-300 focus:outline-none" />
                <input value={age} onChange={(e) => setAge(e.target.value)} placeholder="Age" data-testid="member-age-input" className="rounded-xl border border-cyan-400/25 bg-deep/70 px-4 py-3 text-white placeholder:text-slate-400 focus:border-cyan-300 focus:outline-none sm:w-24" />
                <button onClick={add} data-testid="add-member-btn" className="btn-ghost !py-3"><Plus className="h-4 w-4" /> Add</button>
              </div>
            </div>
            <div>
              <p className="eyebrow mb-4">Dietary needs</p>
              <div className="flex flex-wrap gap-2">{DIETS.map((d) => <Chip key={d} on={diets.includes(d)} onClick={() => setDiets((l) => toggle(l, d))} testId={`diet-${d.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{d}</Chip>)}</div>
            </div>
            <div>
              <p className="eyebrow mb-4">Family goals</p>
              <div className="flex flex-wrap gap-2">{GOALS.map((g) => <Chip key={g} on={goals.includes(g)} onClick={() => setGoals((l) => toggle(l, g))} testId={`goal-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{g}</Chip>)}</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div data-testid="profile-preview" className="sticky top-28 rounded-3xl border border-cyan-300/40 bg-gradient-to-br from-blue-950/80 to-deep p-6 shadow-[0_0_40px_rgba(34,211,238,0.18)]">
              <p className="eyebrow">What Honesti would know</p>
              <p className="mt-4 font-display text-3xl font-semibold text-white">Household of {members.length}</p>
              <ul className="mt-5 space-y-3 text-sm text-slate-200">
                <li><span className="text-slate-400">Members — </span>{members.map((m) => m.name).join(", ") || "None yet"}</li>
                <li><span className="text-slate-400">Always respect — </span>{diets.join(", ") || "No restrictions"}</li>
                <li><span className="text-slate-400">Working toward — </span>{goals.join(", ") || "No goals yet"}</li>
              </ul>
              <p className="mt-6 rounded-xl bg-white/5 p-4 text-sm italic text-cyan-100">“Got it — {diets.length ? `I’ll keep every idea ${diets[0].toLowerCase()}` : "no restrictions noted"}{goals[0] ? ` and focus on ${goals[0].toLowerCase()}` : ""}.”</p>
              <button onClick={save} disabled={saving} data-testid="save-profile-btn" className="btn-primary mt-6 w-full disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save profile"}</button>
              {savedAt && (
                <div className="mt-4 flex items-center justify-between gap-3 text-xs text-slate-300" data-testid="profile-saved-status">
                  <span>Saved {new Date(savedAt).toLocaleString()} · <Link to="/features/meal-recommendations" className="text-cyan-200 underline-offset-2 hover:underline">Get meal ideas</Link></span>
                  <button onClick={forget} data-testid="forget-profile-btn" className="flex items-center gap-1 text-rose-300 hover:text-rose-200"><Trash2 className="h-3.5 w-3.5" /> Forget</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </DemoPanel>
    </FeatureShell>
  );
}
